import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brandVar, loadFont } from "@/lib/color";
import { useAuth } from "@/context/AuthContext";

const BrandContext = createContext(null);
const BRAND_KEY = "cias-brand";
const THEME_KEY = "cias-theme";

export function applyEditionTheme(edition, dark) {
  if (!edition) return;
  const root = document.documentElement;
  const c = edition.colors || {};
  const set = (name, hex) => {
    const v = brandVar(hex, dark);
    root.style.setProperty(`--brand-${name}`, v.value);
    root.style.setProperty(`--brand-${name}-fg`, v.fg);
  };
  set("primary", c.primary);
  set("accent", c.accent);
  set("data", c.data);
  set("action", c.action || "#D99A1E");
  loadFont(edition.heading_font);
  loadFont(edition.body_font);
  root.style.setProperty("--font-heading", `"${edition.heading_font || "Chivo"}", sans-serif`);
  root.style.setProperty("--font-body", `"${edition.body_font || "IBM Plex Sans"}", sans-serif`);
}

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [edition, setEdition] = useState(null);
  const [chosen, setChosen] = useState(() => localStorage.getItem(BRAND_KEY));
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "light");

  const slug = chosen || (user && user.edition_slug) || "";

  const reload = useCallback(async () => {
    const { data } = await api.get("/editions/resolve", { params: slug ? { slug } : {} });
    setEdition(data);
    document.title = `${data.course_title} · ${data.edition_label}`;
  }, [slug]);

  useEffect(() => {
    reload().catch(() => setEdition(null));
  }, [reload]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(THEME_KEY, theme);
    applyEditionTheme(edition, theme === "dark");
  }, [theme, edition]);

  const chooseBrand = (s) => {
    if (s) localStorage.setItem(BRAND_KEY, s);
    else localStorage.removeItem(BRAND_KEY);
    setChosen(s || null);
  };

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <BrandContext.Provider value={{ edition, slug: edition?.slug, theme, toggleTheme, chooseBrand, reload }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);
